import { messaging, getToken, onMessage } from '../firebase/firebaseConfig';
import { env } from '../utils/env';

// Your Web Push Certificate from Firebase Console
// Get this from: Firebase Console > Project Settings > Cloud Messaging > Web Push certificates
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || 'YOUR_VAPID_KEY_HERE';

class NotificationService {
  constructor() {
    this.token = null;
    this.isSupported = this.checkSupport();
  }

  // Check if notifications are supported
  checkSupport() {
    if (typeof window === 'undefined') return false;
    return 'Notification' in window && 
           'serviceWorker' in navigator && 
           'PushManager' in window &&
           messaging !== null;
  }

  // Get current notification permission status
  getPermissionStatus() {
    if (!this.isSupported) return 'unsupported';
    return Notification.permission;
  }

  // Request notification permission
  async requestPermission() {
    if (!this.isSupported) {
      throw new Error('Notifications are not supported in this browser');
    }

    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        console.log('Notification permission granted');
        return true;
      } else if (permission === 'denied') {
        console.log('Notification permission denied');
        return false;
      } else {
        console.log('Notification permission dismissed');
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      throw error;
    }
  }

  // Get FCM token
  async getFCMToken() {
    if (!this.isSupported) {
      throw new Error('Notifications are not supported');
    }

    try {
      console.log('🎟️ Getting FCM token...');
      
      // Request permission first if not granted
      if (Notification.permission !== 'granted') {
        console.log('⚠️ Permission not granted, requesting...');
        const granted = await this.requestPermission();
        if (!granted) {
          throw new Error('Permission not granted');
        }
      } else {
        console.log('✅ Permission already granted');
      }

      // Get registration
      console.log('📝 Getting service worker registration...');
      const registration = await navigator.serviceWorker.ready;
      console.log('✅ Service worker ready:', registration.active?.scriptURL);
      
      // Get token
      console.log('🔑 Requesting FCM token from Firebase...');
      console.log('VAPID Key (full):', VAPID_KEY);
      console.log('VAPID Key length:', VAPID_KEY?.length);
      console.log('Service Worker Registration:', registration.scope);
      
      if (!VAPID_KEY || VAPID_KEY === 'YOUR_VAPID_KEY_HERE') {
        throw new Error('⚠️ VAPID key not configured! Check .env file: REACT_APP_FIREBASE_VAPID_KEY');
      }
      
      const currentToken = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration
      });

      if (currentToken) {
        this.token = currentToken;
        console.log('✅ FCM Token received:', currentToken.substring(0, 30) + '...');
        return currentToken;
      } else {
        console.log('❌ No registration token available');
        return null;
      }
    } catch (error) {
      console.error('❌ Error getting FCM token:', error);
      console.error('Error details:', error.message);
      throw error;
    }
  }

  // Listen for foreground messages
  onMessageListener() {
    if (!this.isSupported) {
      return Promise.reject(new Error('Notifications not supported'));
    }

    return new Promise((resolve) => {
      onMessage(messaging, (payload) => {
        console.log('Message received in foreground:', payload);
        resolve(payload);
      });
    });
  }

  // Show a local notification (for testing or custom notifications)
  showLocalNotification(title, options = {}) {
    if (!this.isSupported) {
      throw new Error('Notifications not supported');
    }

    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        icon: '/logo192.png',
        badge: '/logo192.png',
        ...options
      });

      // Handle notification click
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        notification.close();
        
        // Navigate if URL is provided
        if (options.data?.url) {
          window.location.href = options.data.url;
        }
      };

      return notification;
    } else {
      console.log('Notification permission not granted');
      return null;
    }
  }

  // Save token to backend
  async saveTokenToServer(userId, token) {
    console.log('🚀 saveTokenToServer called with:', { userId, tokenLength: token?.length });
    
    try {
      const endpoint = `${env.API_URL}/notifications/register`;
      const jwtToken = localStorage.getItem('jwt');
      
      console.log('🔍 Environment check:');
      console.log('env.API_URL:', env.API_URL);
      console.log('JWT Token exists:', !!jwtToken);
      
      const requestBody = {
        userId,
        token,
        platform: 'web',
        deviceInfo: {
          userAgent: navigator.userAgent,
          platform: navigator.platform
        }
      };
      
      console.log('📡 Saving token to backend:');
      console.log('URL:', endpoint);
      console.log('Headers:', {
        'Content-Type': 'application/json',
        'Authorization': jwtToken ? `Bearer ${jwtToken.substring(0, 20)}...` : 'NO TOKEN'
      });
      console.log('Body:', {
        ...requestBody,
        token: token.substring(0, 30) + '...' // Truncate for logging
      });
      
      console.log('🌐 Making fetch request...');
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        },
        body: JSON.stringify(requestBody)
      });
      
      console.log('📨 Response received:', response.status, response.statusText);

      const responseData = await response.json();
      console.log('📥 Backend response:', response.status, responseData);

      if (response.ok) {
        console.log('✅ Token saved to server successfully');
        return true;
      } else {
        console.error('❌ Failed to save token to server:', responseData);
        return false;
      }
    } catch (error) {
      console.error('❌ Error saving token to server:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      return false;
    }
  }

  // Delete token from backend (on logout)
  async deleteTokenFromServer(token) {
    try {
      const response = await fetch(`${env.API_URL}/notifications/unregister`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwt')}`
        },
        body: JSON.stringify({ token })
      });

      if (response.ok) {
        console.log('Token deleted from server successfully');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting token from server:', error);
      return false;
    }
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
